/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/**
 * @author Granite Bagas
 */

import React, { useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, ScrollView, Dimensions } from 'react-native';
import * as Yup from 'yup';
import { useFocusEffect } from '@react-navigation/native';

import SociollaText from '@components/sociolla/SociollaText';
import Button from '@components/form/Button';
import * as Layout from '@components/screen/Layout';
import CustomerSearch from '@components/form/search/CustomerSearch';
import { SociollaInput, SociollaCheckbox } from '@components/sociolla/form';
import TitleSubSection from '@components/global/TitleSubSection';
import RenderIf from '@components/global/RenderIf';
import ProgressBar from '@components/global/ProgressBar';

import Colors from '@global/colors';
import { isWeb, isMobileWeb, isAndroid } from '@global/screens';
import { showPopup } from '@/store/modal';
import useApi from '@/hooks/useApi';
import {
  MsOrder,
  createProductListing,
  updateProductListing,
  addProductListingItems,
  updateProductListingItems,
  deleteProductListingItems,
} from '@api/ms-order';
import { getAttibutes } from '@global/helpers';
import { selectParams, paramsReseted } from '@/store/params';
import { REQUEST_PRODUCT_LISTING_ITEMS_FORM } from '@/constants/table-name';

import ModalAddProductListing from './components/ModalAddProductListing';
import ModalProduct from './components/ModalProduct';
import TableProductListingItem from './components/TableProductListingItem';

const FormValidationSchema = Yup.object().shape({
  customer: Yup.object().required('Customer is required.'),
});

const dimension = Dimensions.get('screen');

const TOTAL_PAGE = 2;

const RequestProductListingFormScreen = (props) => {
  const year = props?.route?.params?.year;
  const listingId = props?.route?.params?.id;
  const isEdit = Boolean(listingId);

  const paramsState = useSelector(
    selectParams(REQUEST_PRODUCT_LISTING_ITEMS_FORM),
  );

  const dispatch = useDispatch();
  const formRef = useRef();
  const isActiveRef = useRef(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [detailData, setDetailData] = useState({});
  const [page, setPage] = useState(1);

  const [products, setProducts] = useState([]);
  const [productCount, setProductCount] = useState(0);

  const getProductListingDetail = useApi(MsOrder.getProductListingDetail);
  const getProductListingItems = useApi(MsOrder.getProductListingItems);
  const getProductListingItemsCount = useApi(
    MsOrder.getProductListingItemsCount,
  );

  const getDetail = async (id) => {
    const response = await getProductListingDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      if (!isActiveRef.current) {
        return;
      }
      const result = response.data.data;
      const formattedData = {
        ...result,
        id: result._id,
      };
      setDetailData(formattedData);

      await getProducts(paramsState);
    }
  };

  const getProducts = async (params) => {
    let totalCount = productCount;
    if (params.skip == 0) {
      const countResponse = await getProductListingItemsCount.request(
        listingId,
        { params },
      );
      if (countResponse && countResponse.data && countResponse.data.data) {
        totalCount = countResponse.data.data;
      } else {
        totalCount = 0;
      }
    }
    setProductCount(totalCount);

    const response = await getProductListingItems.request(listingId, {
      params: params,
    });
    const result = response?.data?.data || [];

    const finalResult = result.map((p) => ({
      ...p,
      ref_name: `[${p.combination.reference}] ${p.name}`,
      variant: getAttibutes(p.combination.attributes),
      image: p.combination.image,
      category: p.category.name,
    }));

    if (params.skip > 0 && !isWeb) {
      setProducts((prev) => [...prev, ...finalResult]);
    } else {
      setProducts(finalResult);
    }
  };

  const submitForm = async (payload, { resetForm }) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      let finalPayload = {
        customer: {
          id: payload.customer.id,
          code: payload.customer.code,
          name: payload.customer.name,
          is_subdistributor: payload.customer.is_sub_distributor,
          image:
            Array.isArray(payload.customer.image) &&
            payload.customer.image.length
              ? payload.customer.image[0]
              : !Array.isArray(payload.customer.image) && payload.customer.image
              ? payload.customer.image
              : '',
          market_type: {
            id: payload.customer.market_type.id,
            code: payload.customer.market_type.code,
            cerebro_id: payload.customer.market_type.cerebro_id,
          },
          sales_team: {
            id: payload.customer.sales_team.id,
            name: payload.customer.sales_team.name,
          },
          key_account_manager: (payload.customer.key_account_manager || []).map(
            (kam) => ({
              id: kam.id,
              name: kam.name,
            }),
          ),
          account_manager: (payload.customer.account_manager || []).map(
            (am) => ({
              id: am.id,
              name: am.name,
            }),
          ),
        },
        is_active: payload.is_active,
        year: Number(year),
      };
      if (payload.product && Object.keys(payload.product).length) {
        finalPayload.product = payload.product;
      }
      if (payload.url) {
        finalPayload.url = payload.url;
      }

      // Filter out null, undefined, and empty string values
      finalPayload = Object.fromEntries(
        Object.entries(finalPayload).filter(
          ([key, value]) =>
            value !== null && value !== undefined && value !== '',
        ),
      );

      if (!isEdit) {
        const createData = await createProductListing(finalPayload);
        if (createData?.data?.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Product Listing & Summary submitted',
            type: 'success',
          });
          resetForm();
          resetData();
          goBack();
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      } else {
        const updateData = await updateProductListing(
          detailData._id,
          finalPayload,
        );

        if (updateData?.data?.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Successfully edited data',
            type: 'success',
          });
          resetForm();
          resetData();
          goBack();
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const resetData = () => {
    setDetailData({});
    setProducts([]);
    setShowAddProduct(false);
    setShowProductModal(false);
    setPage(1);
    dispatch(paramsReseted({ key: REQUEST_PRODUCT_LISTING_ITEMS_FORM }));
  };

  const goBack = () => {
    resetData();
    if (isEdit) {
      props.navigation.navigate('RequestProductListingDetailScreen', {
        titleScreen: detailData.customer.code,
        name: detailData.customer.code,
        id: detailData._id,
        year: `${detailData.year}`,
        go_back_key: props.route.key,
      });
    } else {
      props.navigation.replace('RequestProductListingFormScreen', {});
      props.navigation.navigate('RequestProductListingScreen', {
        titleScreen: `${year}`,
        name: `${year}`,
        year: `${year}`,
        go_back_key: props.route.key,
      });
    }
  };

  const handleEditProduct = async (prod) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      await updateProductListingItems(listingId, prod.id, prod.combination.id, {
        name: prod.name,
        is_active: !prod.is_active,
      });
      await getProducts(
        isWeb
          ? paramsState
          : {
              filter: {},
              limit: isWeb ? 10 : 15,
              skip: 0,
            },
      );
      Toast.show({
        text1: 'Successfully update product',
        type: 'success',
      });
    } catch (error) {
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const handleDeleteProduct = async (prod) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      await deleteProductListingItems(listingId, prod.id, prod.combination.id);
      await getProducts({ filter: {}, limit: isWeb ? 10 : 15, skip: 0 });
      Toast.show({
        text1: 'Successfully delete product',
        type: 'success',
      });
    } catch (error) {
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const handleAddProductModal = () => {
    setShowAddProduct(true);
  };

  const handleCloseAddProduct = () => {
    setShowAddProduct(false);
  };

  const handleCloseProductModal = () => {
    setShowProductModal(false);
  };

  const handleAddManually = () => {
    setShowAddProduct(false);
    setShowProductModal(true);
  };

  const handleSubmitAddProduct = async (prod) => {
    const product = {
      id: prod.id,
      combination_id: prod.product_combination_id,
    };
    try {
      if (isEdit) {
        // Add product to existing product listing
        dispatch(showPopup({ name: 'isLoading', value: true }));

        await addProductListingItems(listingId, { product });
        await getProducts(paramsState);

        setShowProductModal(false);
      } else {
        // Add product and create product listing
        if (formRef.current) {
          await formRef.current.setFieldValue('product', product, true);

          await submitForm(
            {
              ...formRef.current.values,
              product,
            },
            formRef.current,
          );
        }
      }
    } catch (error) {
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const handleAddImport = async (url) => {
    try {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      dispatch(
        showPopup({
          name: 'showImportModalAddProductListing',
          value: false,
        }),
      );

      // Add product csv url
      if (formRef.current) {
        await formRef.current.setFieldValue('url', url, true);

        await submitForm(
          {
            ...formRef.current.values,
            url,
          },
          formRef.current,
        );
      }
    } catch (error) {
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const buildMobileButtons = (formValues, handleSubmit) => {
    if (page === 1) {
      return [
        {
          title: 'Next',
          type: 'primary',
          disabled: !formValues?.customer?.id,
          onPress: (evt) => {
            setPage(2);
          },
        },
      ];
    }

    return [
      {
        title: 'Save Draft',
        type: 'primary',
        onPress: async (evt) => {
          await handleSubmit();
        },
      },
      {
        title: 'Back',
        type: 'default',
        onPress: (evt) => {
          setPage(1);
        },
      },
    ];
  };

  const fetchData = useCallback(async () => {
    resetData();

    if (listingId) {
      await getDetail(listingId);
    }
  }, [listingId]);

  useFocusEffect(
    useCallback(() => {
      isActiveRef.current = true;
      fetchData();

      return () => {
        isActiveRef.current = false;
        resetData();
        formRef.current?.resetForm();
      };
    }, [fetchData]),
  );

  return (
    <Layout.Container>
      <RenderIf condition={!isWeb}>
        <ProgressBar page={page} totalPage={TOTAL_PAGE} />
      </RenderIf>

      {isEdit &&
      (getProductListingDetail?.loading ||
        !detailData ||
        !Object.keys(detailData).length) ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          innerRef={formRef}
          initialValues={{
            customer:
              isEdit &&
              detailData?.customer &&
              Object.keys(detailData.customer).length
                ? detailData.customer
                : '',
            is_active: isEdit && detailData?.is_active ? true : false,
            product: '',
            url: '',
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (values, formikBag) => {
            await submitForm(values, formikBag);
          }}>
          {({ setFieldValue, handleSubmit, errors, touched, values }) => {
            return (
              <>
                {isWeb ? (
                  <Layout.Wrapper white={false}>
                    <Layout.Back
                      title="Product Listing & Summary"
                      onCallback={() => goBack()}
                    />
                    <Layout.Title
                      title={`${
                        isEdit ? `Edit` : `Add`
                      } Product Listing & Summary`}>
                      <View style={styles.row}>
                        <Button
                          type="primary"
                          rounded
                          buttonStyle={{ width: 120 }}
                          disabledStyle={{ width: 120 }}
                          disabled={!values?.customer?.id}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 14,
                                letterSpacing: 0.5,
                                color: Colors.white,
                              }}>
                              Save
                            </SociollaText>
                          }
                          onPress={(evt) => {
                            handleSubmit(evt);
                          }}
                        />
                      </View>
                    </Layout.Title>
                  </Layout.Wrapper>
                ) : (
                  false
                )}
                <Layout.Wrapper rounded isUndefined>
                  <RenderIf condition={isWeb || (!isWeb && page == 1)}>
                    <ScrollView
                      keyboardShouldPersistTaps="handled"
                      style={
                        isMobileWeb ? { height: dimension.height - 85 } : {}
                      }
                      contentContainerStyle={{ width: '100%' }}>
                      <Layout.FormWrapper
                        style={{
                          paddingHorizontal: 15,
                          paddingTop: 20,
                          ...(isWeb
                            ? {
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                paddingHorizontal: 20,
                                marginBottom: 15,
                              }
                            : {
                                paddingBottom: !isMobileWeb
                                  ? isAndroid
                                    ? 200
                                    : 150
                                  : 320,
                              }),
                        }}>
                        <View style={styles.rowForm}>
                          <Layout.FormGroup>
                            <CustomerSearch
                              fields="_id name customer_code is_sub_distributor image market_type sales_team key_account_manager account_manager"
                              editable={!isEdit}
                              label="Customer"
                              placeholder="Select Customer"
                              labelKey="display_label"
                              searchWithCode={true}
                              value={values.customer}
                              isRequired={true}
                              hasError={
                                errors.customer && touched.customer
                                  ? errors.customer
                                  : null
                              }
                              customFilter={{
                                status: 'approved',
                                is_active: true,
                              }}
                              onChange={async (value) => {
                                await setFieldValue(
                                  'customer',
                                  { ...value, code: value.customer_code },
                                  true,
                                );
                              }}
                            />
                          </Layout.FormGroup>
                          <Layout.FormGroup>
                            <SociollaInput
                              name="customer_code"
                              label="Customer Code"
                              isRequired
                              editable={false}
                              disabled
                              value={values.customer.code || ''}
                            />
                          </Layout.FormGroup>
                        </View>
                        <View style={styles.rowForm}>
                          <Layout.FormGroup>
                            <SociollaInput
                              name="year"
                              label="Periode"
                              isRequired
                              editable={false}
                              disabled
                              value={year}
                            />
                          </Layout.FormGroup>
                          <Layout.FormGroup
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              gap: 20,
                              marginTop: 10,
                            }}>
                            <SociollaText>Is Active</SociollaText>
                            <SociollaCheckbox label="" name="is_active" />
                          </Layout.FormGroup>
                        </View>
                      </Layout.FormWrapper>
                    </ScrollView>
                  </RenderIf>

                  <RenderIf condition={isWeb || (!isWeb && page == 2)}>
                    <View
                      style={{
                        width: '100%',
                        ...(isWeb
                          ? {
                              paddingHorizontal: 20,
                              marginBottom: 15,
                            }
                          : {}),
                      }}>
                      <TitleSubSection
                        title={'PRODUCT'}
                        style={{ marginBottom: 10 }}
                        button={{
                          label: 'Add Product',
                          callback: handleAddProductModal,
                        }}
                        disabled={!values?.customer?.id}
                      />
                      <TableProductListingItem
                        list={products}
                        getProducts={getProducts}
                        productCount={productCount}
                        loading={
                          isEdit &&
                          getProductListingItems.loading &&
                          products.length == 0
                        }
                        editProduct={handleEditProduct}
                        deleteProduct={handleDeleteProduct}
                        isEditMode
                      />
                    </View>
                  </RenderIf>
                </Layout.Wrapper>

                <RenderIf
                  condition={
                    !isWeb &&
                    (isEdit ? !getProductListingDetail?.loading : true)
                  }>
                  <Layout.ButtonSticky
                    show={
                      props?.route?.name == 'RequestProductListingFormScreen'
                    }
                    route={props?.route}
                    buttons={buildMobileButtons(values, handleSubmit)}
                  />
                </RenderIf>
              </>
            );
          }}
        </Formik>
      ) : (
        false
      )}

      <ModalAddProductListing
        show={showAddProduct}
        onClose={handleCloseAddProduct}
        handleAdd={handleAddManually}
        handleAddImport={handleAddImport}
      />
      <ModalProduct
        show={showProductModal}
        onClose={handleCloseProductModal}
        onChange={handleSubmitAddProduct}
        isCatalog={true}
      />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  rowForm: { width: isWeb ? 'calc(50% - 20px)' : '100%' },
});

export default RequestProductListingFormScreen;
